import React, { useState } from 'react';
import {
  Save, DollarSign, RefreshCw, Bell, Mail, Globe, Shield,
  ChevronRight, Check, AlertTriangle, Palette, Users, Upload,
  Trash2, Edit2, Plus, Eye, EyeOff, X, Building
} from 'lucide-react';

// Mock users data
const mockUsers = [
  { id: 1, name: 'John Smith', email: 'john@boltnexus.com', role: 'owner', status: 'active', lastLogin: '2 hours ago' },
  { id: 2, name: 'Sarah Johnson', email: 'sarah@boltnexus.com', role: 'admin', status: 'active', lastLogin: '1 day ago' },
  { id: 3, name: 'Mike Williams', email: 'mike@boltnexus.com', role: 'cashier', status: 'active', lastLogin: '3 hours ago' },
  { id: 4, name: 'Emily Davis', email: 'emily@boltnexus.com', role: 'viewer', status: 'invited', lastLogin: 'Never' },
];

const rolePermissions = {
  owner: ['Full access to all features', 'Manage billing and subscription', 'Delete tenant'],
  admin: ['Manage users and settings', 'View all reports', 'Approve invoices'],
  cashier: ['Process payments', 'Generate invoices', 'View customer data'],
  viewer: ['View dashboards and reports', 'Read-only access']
};

export default function Settings() {
  const [activeTab, setActiveTab] = useState('branding');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [feeSettings, setFeeSettings] = useState({
    bagServiceContribution: 1000,
    singleServiceContribution: 1500,
    bagPalletFee: 1296,
    singlePalletFee: 648,
    deliveryFee: 1000,
    freightRatePerKg: 2.78,
    exchangeRate: 165
  });

  const [syncSettings, setSyncSettings] = useState({
    autoSync: true,
    syncInterval: 15,
    tasokoApiKey: '••••••••••••••••',
    webhookUrl: 'https://api.boltlogistics.com/webhook/tasoko'
  });

  const [brandingSettings, setBrandingSettings] = useState({
    companyName: 'Bolt Nexus',
    logo: null,
    primaryColor: '#3b82f6',
    secondaryColor: '#06b6d4',
    customDomain: '',
    favicon: null
  });

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  const openUserModal = (user = null) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const tabs = [
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'users', label: 'Users & Roles', icon: Users },
    { id: 'fees', label: 'Fee Structure', icon: DollarSign },
    { id: 'sync', label: 'Tasoko Sync', icon: RefreshCw },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'integrations', label: 'Integrations', icon: Globe },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-layout">
        {/* Settings Navigation */}
        <div className="settings-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
              <ChevronRight size={16} className="arrow" />
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="settings-section" data-testid="branding-section">
              <div className="section-header">
                <h2>Branding & White-Label</h2>
                <p>Customize your tenant's appearance and branding for clients.</p>
              </div>

              <div className="settings-card">
                <h3>Company Logo</h3>
                <div className="logo-upload-section">
                  <div className="logo-preview">
                    {brandingSettings.logo ? (
                      <img src={brandingSettings.logo} alt="Company Logo" />
                    ) : (
                      <div className="logo-placeholder">
                        <Building size={32} />
                        <span>No logo uploaded</span>
                      </div>
                    )}
                  </div>
                  <div className="logo-upload-actions">
                    <button className="btn btn-secondary" data-testid="upload-logo-btn">
                      <Upload size={14} /> Upload Logo
                    </button>
                    {brandingSettings.logo && (
                      <button className="btn btn-ghost" data-testid="remove-logo-btn">
                        <Trash2 size={14} /> Remove
                      </button>
                    )}
                    <p className="upload-hint">Recommended: PNG or SVG, 200x60px minimum</p>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Company Information</h3>
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={brandingSettings.companyName}
                    onChange={(e) => setBrandingSettings({...brandingSettings, companyName: e.target.value})}
                    data-testid="company-name-input"
                  />
                </div>
              </div>

              <div className="settings-card">
                <h3>Brand Colors</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        value={brandingSettings.primaryColor}
                        onChange={(e) => setBrandingSettings({...brandingSettings, primaryColor: e.target.value})}
                        className="color-picker"
                        data-testid="primary-color-input"
                      />
                      <input
                        type="text"
                        value={brandingSettings.primaryColor}
                        onChange={(e) => setBrandingSettings({...brandingSettings, primaryColor: e.target.value})}
                        className="color-text"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Secondary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        value={brandingSettings.secondaryColor}
                        onChange={(e) => setBrandingSettings({...brandingSettings, secondaryColor: e.target.value})}
                        className="color-picker"
                        data-testid="secondary-color-input"
                      />
                      <input
                        type="text"
                        value={brandingSettings.secondaryColor}
                        onChange={(e) => setBrandingSettings({...brandingSettings, secondaryColor: e.target.value})}
                        className="color-text"
                      />
                    </div>
                  </div>
                </div>
                <div className="color-preview-section">
                  <h4>Preview</h4>
                  <div className="color-preview-bar">
                    <div className="preview-block primary" style={{background: brandingSettings.primaryColor}}>Primary</div>
                    <div className="preview-block secondary" style={{background: brandingSettings.secondaryColor}}>Secondary</div>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Custom Domain</h3>
                <p className="card-description">
                  Configure a custom domain for your customer portal (requires DNS setup).
                </p>
                <div className="form-group">
                  <label>Domain</label>
                  <div className="input-with-prefix">
                    <span>https://</span>
                    <input
                      type="text"
                      placeholder="portal.yourcompany.com"
                      value={brandingSettings.customDomain}
                      onChange={(e) => setBrandingSettings({...brandingSettings, customDomain: e.target.value})}
                      data-testid="custom-domain-input"
                    />
                  </div>
                </div>
                <div className="domain-status pending">
                  <AlertTriangle size={16} />
                  <span>DNS verification pending</span>
                </div>
              </div>

              <div className="settings-card">
                <h3>Favicon</h3>
                <div className="favicon-upload">
                  <div className="favicon-preview">
                    {brandingSettings.favicon ? (
                      <img src={brandingSettings.favicon} alt="Favicon" />
                    ) : (
                      <div className="favicon-placeholder">?</div>
                    )}
                  </div>
                  <button className="btn btn-secondary" data-testid="upload-favicon-btn">
                    <Upload size={14} /> Upload Favicon
                  </button>
                  <p className="upload-hint">32x32px PNG or ICO</p>
                </div>
              </div>
            </div>
          )}

          {/* Users & Roles Tab */}
          {activeTab === 'users' && (
            <div className="settings-section" data-testid="users-section">
              <div className="section-header">
                <h2>Users & Roles</h2>
                <p>Manage team members and their access permissions.</p>
              </div>

              {/* Users Table */}
              <div className="settings-card users-table-card">
                <div className="users-header">
                  <h3>Team Members</h3>
                  <button 
                    className="btn btn-primary"
                    onClick={() => openUserModal()}
                    data-testid="invite-user-btn"
                  >
                    <Plus size={14} /> Invite User
                  </button>
                </div>

                <div className="users-table">
                  <div className="users-table-header">
                    <span>User</span>
                    <span>Role</span>
                    <span>Status</span>
                    <span>Last Login</span>
                    <span>Actions</span>
                  </div>
                  {mockUsers.map((user) => (
                    <div key={user.id} className="user-row" data-testid={`user-${user.id}`}>
                      <div className="user-info">
                        <div className="user-avatar">{user.name.split(' ').map(n => n[0]).join('')}</div>
                        <div>
                          <span className="user-name">{user.name}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </div>
                      <div className="user-role">
                        <span className={`role-badge ${user.role}`}>{user.role}</span>
                      </div>
                      <div className="user-status">
                        <span className={`status-dot ${user.status}`}></span>
                        <span>{user.status}</span>
                      </div>
                      <div className="user-last-login">{user.lastLogin}</div>
                      <div className="user-actions">
                        <button 
                          className="action-btn"
                          onClick={() => openUserModal(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        {user.role !== 'owner' && (
                          <button className="action-btn delete" data-testid={`delete-user-${user.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Roles Reference */}
              <div className="settings-card">
                <h3>Role Permissions Reference</h3>
                <div className="roles-grid">
                  {Object.entries(rolePermissions).map(([role, permissions]) => (
                    <div key={role} className="role-card">
                      <h4 className={`role-title ${role}`}>{role}</h4>
                      <ul className="permissions-list">
                        {permissions.map((perm, i) => (
                          <li key={i}>
                            <Check size={12} />
                            <span>{perm}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'fees' && (
            <div className="settings-section" data-testid="fees-section">
              <div className="section-header">
                <h2>Fee Structure</h2>
                <p>Configure the fee schedule for bag and single item invoicing.</p>
              </div>

              <div className="settings-card">
                <h3>Service Contribution</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bag Rate (JMD)</label>
                    <div className="input-with-prefix">
                      <span>$</span>
                      <input
                        type="number"
                        value={feeSettings.bagServiceContribution}
                        onChange={(e) => setFeeSettings({...feeSettings, bagServiceContribution: Number(e.target.value)})}
                        data-testid="bag-service-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Single Rate (JMD)</label>
                    <div className="input-with-prefix">
                      <span>$</span>
                      <input
                        type="number"
                        value={feeSettings.singleServiceContribution}
                        onChange={(e) => setFeeSettings({...feeSettings, singleServiceContribution: Number(e.target.value)})}
                        data-testid="single-service-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Pallet Fee</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bag Rate (JMD)</label>
                    <div className="input-with-prefix">
                      <span>$</span>
                      <input
                        type="number"
                        value={feeSettings.bagPalletFee}
                        onChange={(e) => setFeeSettings({...feeSettings, bagPalletFee: Number(e.target.value)})}
                        data-testid="bag-pallet-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Single Rate (JMD)</label>
                    <div className="input-with-prefix">
                      <span>$</span>
                      <input
                        type="number"
                        value={feeSettings.singlePalletFee}
                        onChange={(e) => setFeeSettings({...feeSettings, singlePalletFee: Number(e.target.value)})}
                        data-testid="single-pallet-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Delivery Fee</h3>
                <div className="form-group">
                  <label>Flat Rate (JMD)</label>
                  <div className="input-with-prefix">
                    <span>$</span>
                    <input
                      type="number"
                      value={feeSettings.deliveryFee}
                      onChange={(e) => setFeeSettings({...feeSettings, deliveryFee: Number(e.target.value)})}
                      data-testid="delivery-fee-input"
                    />
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Freight Calculation</h3>
                <div className="formula-display">
                  <code>Freight (JMD) = (Weight ÷ 2.2) × Rate × Exchange</code>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rate per kg (USD)</label>
                    <div className="input-with-prefix">
                      <span>$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={feeSettings.freightRatePerKg}
                        onChange={(e) => setFeeSettings({...feeSettings, freightRatePerKg: Number(e.target.value)})}
                        data-testid="freight-rate-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>USD to JMD Exchange Rate</label>
                    <input
                      type="number"
                      value={feeSettings.exchangeRate}
                      onChange={(e) => setFeeSettings({...feeSettings, exchangeRate: Number(e.target.value)})}
                      data-testid="exchange-rate-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="settings-section" data-testid="sync-section">
              <div className="section-header">
                <h2>Tasoko Sync Settings</h2>
                <p>Configure automatic synchronization with Tasoko warehouse system.</p>
              </div>

              <div className="settings-card">
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Auto-Sync</h4>
                    <p>Automatically pull manifest data from Tasoko at regular intervals.</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={syncSettings.autoSync}
                      onChange={(e) => setSyncSettings({...syncSettings, autoSync: e.target.checked})}
                      data-testid="auto-sync-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {syncSettings.autoSync && (
                  <div className="form-group">
                    <label>Sync Interval (minutes)</label>
                    <select
                      value={syncSettings.syncInterval}
                      onChange={(e) => setSyncSettings({...syncSettings, syncInterval: Number(e.target.value)})}
                      data-testid="sync-interval-select"
                    >
                      <option value={5}>Every 5 minutes</option>
                      <option value={15}>Every 15 minutes</option>
                      <option value={30}>Every 30 minutes</option>
                      <option value={60}>Every hour</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="settings-card">
                <h3>API Configuration</h3>
                <div className="form-group">
                  <label>Tasoko API Key</label>
                  <div className="input-with-action">
                    <input
                      type="password"
                      value={syncSettings.tasokoApiKey}
                      readOnly
                      data-testid="api-key-input"
                    />
                    <button className="btn btn-secondary">Reveal</button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Webhook URL (for Tasoko to call)</label>
                  <div className="input-with-action">
                    <input
                      type="text"
                      value={syncSettings.webhookUrl}
                      readOnly
                      data-testid="webhook-url-input"
                    />
                    <button className="btn btn-secondary">Copy</button>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Connection Status</h3>
                <div className="connection-status connected">
                  <Check size={20} />
                  <span>Connected to Tasoko API</span>
                  <span className="last-sync">Last sync: 2 minutes ago</span>
                </div>
                <button className="btn btn-secondary" data-testid="test-connection-btn">
                  <RefreshCw size={14} /> Test Connection
                </button>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section" data-testid="notifications-section">
              <div className="section-header">
                <h2>Notification Settings</h2>
                <p>Configure how notifications are sent to couriers and customers.</p>
              </div>

              <div className="settings-card">
                <h3>Email Notifications</h3>
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Invoice Emails</h4>
                    <p>Send invoice PDF via email when generated.</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked data-testid="invoice-email-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Payment Confirmations</h4>
                    <p>Send confirmation email when payment is received.</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked data-testid="payment-email-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="settings-card">
                <h3>WhatsApp Notifications</h3>
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Package Ready Alerts</h4>
                    <p>Notify customers when packages are ready for pickup.</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked data-testid="whatsapp-pickup-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="settings-card">
                <h3>SMS Notifications</h3>
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Payment Reminders</h4>
                    <p>Send SMS reminders for overdue invoices.</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked data-testid="sms-reminder-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="settings-section" data-testid="integrations-section">
              <div className="section-header">
                <h2>Integrations</h2>
                <p>Connect with external services and APIs.</p>
              </div>

              <div className="integration-cards">
                <div className="integration-card connected">
                  <div className="integration-logo tasoko">T</div>
                  <div className="integration-info">
                    <h4>Tasoko</h4>
                    <p>Warehouse management system</p>
                  </div>
                  <span className="integration-status">
                    <Check size={14} /> Connected
                  </span>
                </div>

                <div className="integration-card connected">
                  <div className="integration-logo sendgrid">SG</div>
                  <div className="integration-info">
                    <h4>SendGrid</h4>
                    <p>Email delivery service</p>
                  </div>
                  <span className="integration-status">
                    <Check size={14} /> Connected
                  </span>
                </div>

                <div className="integration-card connected">
                  <div className="integration-logo whatsapp">WA</div>
                  <div className="integration-info">
                    <h4>WhatsApp Business</h4>
                    <p>WhatsApp messaging API</p>
                  </div>
                  <span className="integration-status">
                    <Check size={14} /> Connected
                  </span>
                </div>

                <div className="integration-card">
                  <div className="integration-logo stripe">S</div>
                  <div className="integration-info">
                    <h4>Stripe</h4>
                    <p>Payment processing</p>
                  </div>
                  <button className="btn btn-secondary" data-testid="connect-stripe-btn">Connect</button>
                </div>

                <div className="integration-card">
                  <div className="integration-logo quickbooks">QB</div>
                  <div className="integration-info">
                    <h4>QuickBooks</h4>
                    <p>Accounting software</p>
                  </div>
                  <button className="btn btn-secondary" data-testid="connect-quickbooks-btn">Connect</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section" data-testid="security-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <p>Manage account security and access controls.</p>
              </div>

              <div className="settings-card">
                <h3>Change Password</h3>
                <div className="form-group">
                  <label>Current Password</label>
                  <input type="password" placeholder="••••••••" data-testid="current-password-input" />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input type="password" placeholder="••••••••" data-testid="new-password-input" />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input type="password" placeholder="••••••••" data-testid="confirm-password-input" />
                </div>
                <button className="btn btn-secondary" data-testid="update-password-btn">Update Password</button>
              </div>

              <div className="settings-card">
                <h3>Two-Factor Authentication</h3>
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Enable 2FA</h4>
                    <p>Add an extra layer of security to your account.</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" data-testid="2fa-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="settings-actions">
            <button 
              className={`btn btn-primary ${saved ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="spinning" /> Saving...
                </>
              ) : saved ? (
                <>
                  <Check size={16} /> Saved!
                </>
              ) : (
                <>
                  <Save size={16} /> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div 
            className="modal-content user-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="user-modal"
          >
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Invite New User'}</h3>
              <button className="close-btn" onClick={() => setShowUserModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="Enter full name"
                  defaultValue={editingUser?.name || ''}
                  data-testid="user-name-input"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="email@company.com"
                  defaultValue={editingUser?.email || ''}
                  data-testid="user-email-input"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select 
                  defaultValue={editingUser?.role || 'viewer'}
                  data-testid="user-role-select"
                >
                  <option value="admin">Admin</option>
                  <option value="cashier">Cashier</option>
                  <option value="viewer">Viewer</option>
                </select>
                <p className="field-hint">
                  {editingUser?.role === 'owner' ? 
                    'Owner role cannot be changed' : 
                    'Choose the appropriate access level for this user'}
                </p>
              </div>

              {editingUser && (
                <div className="form-group">
                  <label>Status</label>
                  <select 
                    defaultValue={editingUser?.status || 'active'}
                    data-testid="user-status-select"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="save-user-btn">
                {editingUser ? 'Save Changes' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
